import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives, resolveComponent as _resolveComponent } from "vue"
import _imports_0 from '@/assets/images/backgrounds/redhead-woman-in-green.png'


const _hoisted_1 = {
  key: 0,
  class: "min-h-screen bg-gray-100 flex items-center justify-center"
}
const _hoisted_2 = {
  key: 1,
  class: "flex min-h-screen flex-1 bg-gray-100"
}
const _hoisted_3 = { class: "relative flex flex-1 flex-col justify-center px-6 py-8 sm:px-6 sm:py-12 lg:flex-none lg:px-12 xl:px-20 md:min-w-[50vw]" }
const _hoisted_4 = { class: "mt-10 sm:mx-auto sm:w-full sm:max-w-md" }
const _hoisted_5 = { class: "text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 leading-tight" }
const _hoisted_6 = { class: "mt-3 text-base text-gray-500 leading-relaxed" }
const _hoisted_7 = { class: "mt-8 space-y-4" }
const _hoisted_8 = { class: "flex items-center gap-3" }
const _hoisted_9 = { class: "flex size-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600" }
const _hoisted_10 = { class: "text-sm font-semibold text-gray-900" }
const _hoisted_11 = { class: "text-xs text-gray-500" }
const _hoisted_12 = { class: "flex items-center gap-3" }
const _hoisted_13 = { class: "flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600" }
const _hoisted_14 = { class: "text-sm font-semibold text-gray-900" }
const _hoisted_15 = { class: "text-xs text-gray-500" }
const _hoisted_16 = { class: "flex items-center gap-3" }
const _hoisted_17 = { class: "flex size-10 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600" }
const _hoisted_18 = { class: "text-sm font-semibold text-gray-900" }
const _hoisted_19 = { class: "text-xs text-gray-500" }
const _hoisted_20 = { class: "mt-8 rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 border-t-4 border-teal-500 px-6 py-8 sm:px-8" }
const _hoisted_21 = {
  for: "bio-username",
  class: "block text-sm font-medium text-gray-900 mb-2"
}
const _hoisted_22 = { class: "flex items-center rounded-lg border border-gray-200 bg-gray-50 overflow-hidden focus-within:border-teal-500 focus-within:ring-1 focus-within:ring-teal-500 transition-colors" }
const _hoisted_23 = ["placeholder"]
const _hoisted_24 = { class: "text-xs text-gray-400 mt-2 mb-6" }
const _hoisted_25 = ["disabled"]
const _hoisted_26 = {
  key: 0,
  class: "h-4 w-4 animate-spin",
  viewBox: "0 0 24 24",
  fill: "none"
}
const _hoisted_27 = { class: "mt-4 text-center text-xs text-gray-400" }
const _hoisted_28 = { class: "hidden w-0 flex-1 lg:block" }
const _hoisted_29 = { class: "fixed inset-y-0 right-0 w-[50vw]" }
const _hoisted_30 = { class: "absolute bottom-8 left-8 right-8" }
const _hoisted_31 = { class: "text-white/70 text-sm mt-1 drop-shadow" }
const _hoisted_32 = { key: 2 }

import {
  ref, computed, onBeforeMount,
} from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import useUserStore from '@/store/user';
import UserBio from '@/models/user/bio/userBio';
import Profile from '@/models/user/bio/profile';
import Style from '@/models/user/bio/style';
import {
  ArrowLeftIcon,
  LinkIcon,
  ShoppingBagIcon,
  SparklesIcon,
} from '@heroicons/vue/24/outline';


export default /*@__PURE__*/_defineComponent({
  __name: 'BioManagerLayout',
  setup(__props) {

const router = useRouter();
const { t, tm } = useI18n();
const toast = useToast();
const {
  usersDoLoadBio,
  usersDoGetMyUser,
  usersDoUpdateMyUser,
  usersDoUpdateMyBio,
} = useUserStore();

/*
  Data
*/
const isLoading = ref(true);
const isCreating = ref(false);
const myProfile = ref<any>({});
const myBio = ref<any>({});
const newUsername = ref('');

/*
  Computed
*/
const hasUsername = computed(
  () => !!myProfile.value?.username,
);
const hasBio = computed(
  () => hasUsername.value && !!myBio.value?.id,
);

const randomTagline = computed(() => {
  const list = tm('session.taglines');
  return Array.isArray(list)
    ? list[Math.floor(Math.random() * list.length)]
    : '';
});

/*
  Methods
*/
const load = async () => {
  isLoading.value = true;
  try {
    // Step 1: Get user profile
    const profile = await usersDoGetMyUser();
    if (!profile) {
      router.push({ name: 'DashboardPage' });
      return;
    }
    myProfile.value = profile;

    // Step 2: If user has a username, load or auto-create bio
    if (profile.username) {
      const cleanUsername = profile.username.replace('@', '');
      let bio = null;

      try {
        bio = await usersDoLoadBio(cleanUsername);
      } catch {
        // 404 — bio document doesn't exist in MongoDB
      }

      if (bio) {
        // Bio exists — use it
        myBio.value = bio;
      } else {
        // Username exists but no bio doc — auto-create it
        const newBio = new UserBio({
          username: cleanUsername,
          style: Style.newStyle(),
          live: null,
          profile: Profile.newProfile(profile.name || cleanUsername),
          links: [],
        });
        try {
          await usersDoUpdateMyBio(newBio);
          // Re-load to get the MongoDB _id
          const created = await usersDoLoadBio(cleanUsername);
          myBio.value = created || newBio;
        } catch {
          // If auto-create fails, show empty state
          myBio.value = newBio;
        }
      }
    }
    // If no username, hasBio = false → shows onboarding
  } catch (err: any) {
    console.error('Bio load error:', err);
    if (err?.response?.status === 401) {
      router.push({ name: 'SessionLoginPage' });
    }
  } finally {
    isLoading.value = false;
  }
};

const createBio = async () => {
  isCreating.value = true;
  try {
    const username = newUsername.value.trim().toLowerCase();

    // Step 1: Save username to user profile (postgres)
    await usersDoUpdateMyUser({
      username: `@${username}`,
    });

    // Step 2: Create bio document in MongoDB
    const newBio = new UserBio({
      username,
      style: Style.newStyle(),
      live: null,
      profile: Profile.newProfile(myProfile.value?.name || username),
      links: [],
    });
    await usersDoUpdateMyBio(newBio);

    // Step 3: Refresh state so myBio.value picks up the persisted doc with its _id
    toast.success(t('bio.saved'));
    await load();
  } catch (err: any) {
    const msg = err?.response?.data?.error
      || t('bio.saveError');
    toast.error(msg);
    isCreating.value = false;
  }
};

const reload = async () => {
  await load();
};

/*
  Hooks
*/
onBeforeMount(() => {
  load();
});

return (_ctx: any,_cache: any) => {
  const _component_RouterView = _resolveComponent("RouterView")!

  return (isLoading.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, _cache[3] || (_cache[3] = [
        _createElementVNode("div", { class: "h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-teal-600" }, null, -1)
      ])))
    : (!hasBio.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("button", {
              class: "absolute top-4 left-4 flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors z-10",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push({ name: 'DashboardPage' })))
            }, [
              _createVNode(_unref(ArrowLeftIcon), { class: "size-4" }),
              _createTextVNode(" " + _toDisplayString(_unref(t)('profile.personal.cancel')), 1)
            ]),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("h1", _hoisted_5, _toDisplayString(_unref(t)('bio.create.headline')), 1),
              _createElementVNode("p", _hoisted_6, _toDisplayString(_unref(t)('bio.create.subheadline')), 1),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    _createVNode(_unref(LinkIcon), { class: "size-5" })
                  ]),
                  _createElementVNode("div", null, [
                    _createElementVNode("p", _hoisted_10, _toDisplayString(_unref(t)('bio.create.feature1Title')), 1),
                    _createElementVNode("p", _hoisted_11, _toDisplayString(_unref(t)('bio.create.feature1Desc')), 1)
                  ])
                ]),
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("div", _hoisted_13, [
                    _createVNode(_unref(ShoppingBagIcon), { class: "size-5" })
                  ]),
                  _createElementVNode("div", null, [
                    _createElementVNode("p", _hoisted_14, _toDisplayString(_unref(t)('bio.create.feature2Title')), 1),
                    _createElementVNode("p", _hoisted_15, _toDisplayString(_unref(t)('bio.create.feature2Desc')), 1)
                  ])
                ]),
                _createElementVNode("div", _hoisted_16, [
                  _createElementVNode("div", _hoisted_17, [
                    _createVNode(_unref(SparklesIcon), { class: "size-5" })
                  ]),
                  _createElementVNode("div", null, [
                    _createElementVNode("p", _hoisted_18, _toDisplayString(_unref(t)('bio.create.feature3Title')), 1),
                    _createElementVNode("p", _hoisted_19, _toDisplayString(_unref(t)('bio.create.feature3Desc')), 1)
                  ])
                ])
              ]),
              _createElementVNode("div", _hoisted_20, [
                _createElementVNode("label", _hoisted_21, _toDisplayString(_unref(t)('bio.create.chooseUsername')), 1),
                _createElementVNode("div", _hoisted_22, [
                  _cache[4] || (_cache[4] = _createElementVNode("span", { class: "pl-3 text-sm text-gray-400 select-none whitespace-nowrap" }, "@", -1)),
                  _withDirectives(_createElementVNode("input", {
                    id: "bio-username",
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newUsername).value = $event)),
                    type: "text",
                    maxlength: "30",
                    placeholder: _unref(t)('bio.create.usernamePlaceholder'),
                    class: "flex-1 bg-transparent px-2 py-2.5 text-sm outline-none text-gray-900 placeholder-gray-400"
                  }, null, 8, _hoisted_23), [
                    [_vModelText, newUsername.value]
                  ])
                ]),
                _createElementVNode("p", _hoisted_24, _toDisplayString(newUsername.value.length) + "/30 · " + _toDisplayString(_unref(t)('bio.create.usernameHint')), 1),
                _createElementVNode("button", {
                  class: "flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed",
                  disabled: isCreating.value || newUsername.value.length < 3,
                  onClick: createBio
                }, [
                  (isCreating.value)
                    ? (_openBlock(), _createElementBlock("svg", _hoisted_26, _cache[5] || (_cache[5] = [
                        _createElementVNode("circle", {
                          class: "opacity-25",
                          cx: "12",
                          cy: "12",
                          r: "10",
                          stroke: "currentColor",
                          "stroke-width": "4"
                        }, null, -1),
                        _createElementVNode("path", {
                          class: "opacity-75",
                          fill: "currentColor",
                          d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        }, null, -1)
                      ])))
                    : _createCommentVNode("", true),
                  _createTextVNode(" " + _toDisplayString(_unref(t)('bio.create.create')), 1)
                ], 8, _hoisted_25)
              ]),
              _createElementVNode("p", _hoisted_27, " livelink.com.br/@" + _toDisplayString(newUsername.value || '...'), 1)
            ])
          ]),
          _createElementVNode("div", _hoisted_28, [
            _createElementVNode("div", _hoisted_29, [
              _cache[7] || (_cache[7] = _createElementVNode("img", {
                class: "absolute inset-0 size-full object-cover",
                src: _imports_0,
                loading: "lazy",
                alt: "Woman with products"
              }, null, -1)),
              _cache[8] || (_cache[8] = _createElementVNode("div", { class: "absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/10" }, null, -1)),
              _createElementVNode("div", _hoisted_30, [
                _cache[6] || (_cache[6] = _createElementVNode("p", { class: "text-white/90 text-lg font-semibold drop-shadow-lg" }, " LIVELINK ", -1)),
                _createElementVNode("p", _hoisted_31, _toDisplayString(randomTagline.value), 1)
              ])
            ])
          ])
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_32, [
          _createVNode(_component_RouterView, {
            modelValue: myBio.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((myBio).value = $event)),
            "onUpdate:myBio": reload
          }, null, 8, ["modelValue"])
        ]))
}
}

})